import React, { useState } from 'react'

import { useIntl } from 'react-intl'
import {
	LoanRole,
	UserGroup,
	UserGroupAccessScope,
	CreateAccessScopeRequest,
} from '@matech/thebigpos-sdk'
import { captureException } from '@sentry/react'
import { TableColumn } from 'react-data-table-component'
import { useMutation, useQuery } from '@tanstack/react-query'

import {
	DEFAULT_SORT_BY,
	DEFAULT_SORT_DIRECTION,
} from '../../../constants/pagination'
import { useAlert } from '../../../hooks'
import useSearch from '../../../hooks/useSearch'
import queryKeys from '../../../services/queryKeys'
import DataTable from '../../../components/DataTable'
import { formatDate } from '../../../services/helper'
import usePagination from '../../../hooks/usePagination'
import ModalUserGroupsMembers, {
	type UserGroupFormData,
} from '../../../components/modals/ModalUserGroupsMembers'
import { TheBigPOSApi } from '../../../lib/TheBigPOSClient'
import ModalUserGroupsAccess, {
	type UserGroupScopeItem,
} from './components/ModalUserGroupsAccess/ModalUserGroupsAccess'
import ModalUserGroupsDelete from './components/ModalUserGroupsDelete'
import type { UserRole } from '../../../components/modals/ModalShareLoan'
import convertGroupMembersToUserRoles from './utils/convertGroupMembersToUserRoles'

type UserGroupListItem = Pick<
	UserGroup,
	'id' | 'name' | 'description' | 'createdAt'
>

export type UserGroupItem = UserGroupListItem & {
	totalMembers?: number
	totalAccess?: number
	users?: UserRole[]
	accessItems?: UserGroupScopeItem[]
}

type CreateUserGroupMutationParams = {
	name: string
	description: string
}

type UpdateUserGroupMutationParams = CreateUserGroupMutationParams & {
	groupId: string
}

type RemoveUserGroupMemberMutationParams = {
	groupId: string
	userId: string
}

type CreateUserGroupMemberMutationParams =
	RemoveUserGroupMemberMutationParams & {
		loanRole: LoanRole
	}

type CreateUserGroupScopeMutationParams = CreateAccessScopeRequest & {
	groupId: string
}

const convertGroupScopesToUserGroupScopeItems = (
	data: UserGroupAccessScope[]
) => {
	return data.map((item) => {
		return {
			id: item.id,
			type: item.scopeType,
			value:
				item.scopeType === 'User' ? item.user?.id : item.branch?.id,
			label:
				item.scopeType === 'User'
					? item.user?.name
					: item.branch?.name,
		} as UserGroupScopeItem
	})
}

const GroupSharingList = () => {
	const [showModalGroup, setShowModalGroup] = useState(false)
	const [showModalAccess, setShowModalAccess] = useState(false)
	const [showModalDelete, setShowModalDelete] = useState(false)

	const [groupItem, setGroupItem] = useState<
		UserGroupItem | undefined
	>(undefined)
	const [deletingGroup, setDeletingGroup] = useState<
		UserGroupItem | undefined
	>(undefined)
	const [isDeletingGroup, setIsDeletingGroup] = useState(false)
	const { alert } = useAlert()
	const { formatMessage } = useIntl()

	const {
		searchText,
		debouncedSearchText,
		handleSearchChange,
		handleClearSearchClick,
	} = useSearch()

	const {
		// sortBy,
		sortDirection,
		pageSize,
		pageNumber,
		handleRowsPerPageChange,
		handlePageChange,
		handleSort,
	} = usePagination<UserGroupItem>({
		defaultSortBy: DEFAULT_SORT_BY,
		defaultSortDirection: DEFAULT_SORT_DIRECTION,
	})

	const tableTitle = formatMessage({
		id: 'user_groups.title',
	})

	const queryParams = {
		searchText: debouncedSearchText,
		pageNumber,
		pageSize,
		sortBy: '',
		sortDirection,
	}

	// Query for List
	const {
		data: userGroups,
		refetch: refetchUserGroups,
		isLoading,
	} = useQuery({
		queryKey: [queryKeys.userGroups, queryParams],
		queryFn: async () => {
			const response =
				await TheBigPOSApi.searchUserGroups(queryParams)

			return response.data
		},
	})

	const groupId = groupItem?.id

	// Query for Get Members from item list
	const { data: groupMembers, isFetching: isFetchingGroupMembers } =
		useQuery({
			enabled: !!groupItem?.id && showModalGroup,
			queryKey: [queryKeys.userGroupsMembers, groupId],
			queryFn: async () => {
				const response = await TheBigPOSApi.getUserGroupMembers(
					groupItem?.id!
				)

				return response.data
			},
			select: convertGroupMembersToUserRoles,
		})

	// Mutation to create group
	const {
		mutateAsync: createUserGroupMutateAsync,
		isPending: isPendingCreatingGroup,
	} = useMutation({
		mutationFn: async ({
			name,
			description,
		}: CreateUserGroupMutationParams) => {
			const response = await TheBigPOSApi.createUserGroup({
				name,
				description,
			})

			return response.data
		},
	})

	// Mutation to update group
	const {
		mutateAsync: updateUserGroupMutateAsync,
		isPending: isPendingUpdatingGroup,
	} = useMutation({
		mutationFn: async ({
			groupId,
			name,
			description,
		}: UpdateUserGroupMutationParams) => {
			const response = await TheBigPOSApi.updateUserGroup(groupId, {
				name,
				description,
			})

			return response.data
		},
	})

	// Mutation to create group members
	const {
		mutateAsync: createUserGroupMemberMutateAsync,
		isPending: isPendingCreatingGroupMember,
	} = useMutation({
		mutationFn: ({
			groupId,
			userId,
			loanRole,
		}: CreateUserGroupMemberMutationParams) =>
			TheBigPOSApi.createUserGroupMember(groupId, {
				userId,
				loanRole,
			}),
	})

	// Mutation to delete group members
	const {
		mutateAsync: removeUserGroupMemberMutateAsync,
		isPending: isPendingRemoveGroupMember,
	} = useMutation({
		mutationFn: ({
			groupId,
			userId,
		}: RemoveUserGroupMemberMutationParams) =>
			TheBigPOSApi.deleteUserGroupMember(groupId, userId),
	})

	// Query for Get Scopes from item list
	const { data: groupScopes, isFetching: isFetchingGroupScopes } =
		useQuery({
			enabled: !!groupItem?.id && showModalAccess,
			queryKey: [queryKeys.userGroupsScopes, groupId],
			queryFn: async () => {
				const response = await TheBigPOSApi.getUserGroupAccessScopes(
					groupItem?.id!
				)

				return response.data
			},
			select: convertGroupScopesToUserGroupScopeItems,
		})

	// Mutation to create group access scope
	const {
		mutateAsync: createUserGroupScopeMutateAsync,
		isPending: isPendingCreatingGroupScope,
	} = useMutation({
		mutationFn: ({
			groupId,
			scopeType,
			branchId,
			userId,
		}: CreateUserGroupScopeMutationParams) =>
			TheBigPOSApi.createUserGroupAccessScope(groupId, {
				scopeType,
				branchId,
				userId,
			}),
	})

	// Mutation to delete group access scope
	const {
		mutateAsync: removeUserGroupScopeMutateAsync,
		isPending: isPendingRemoveGroupScope,
	} = useMutation({
		mutationFn: ({
			groupId,
			scopeId,
		}: {
			groupId: string
			scopeId: string
		}) => TheBigPOSApi.deleteUserGroupAccessScope(groupId, scopeId),
	})

	const isSubmittingUserGroupMembers =
		isPendingCreatingGroup ||
		isPendingCreatingGroupMember ||
		isPendingUpdatingGroup ||
		isPendingRemoveGroupMember

	const isSubmittingScope =
		isPendingCreatingGroupScope || isPendingRemoveGroupScope

	const isModalGroupMembersVisible =
		showModalGroup && !isFetchingGroupMembers

	const handleAddNewGroup = () => {
		setGroupItem(undefined)
		setShowModalGroup(true)
	}

	const handleEditGroup = async (
		group: UserGroupItem,
		modalType: 'members' | 'access'
	) => {
		setGroupItem(group)
		if (modalType === 'members') {
			setShowModalGroup(true)
		} else if (modalType === 'access') {
			setShowModalAccess(true)
		}
	}

	const handleCloseModal = (
		modalType: 'members' | 'access' | 'delete'
	) => {
		if (modalType === 'delete') {
			setShowModalDelete(false)
			setDeletingGroup(undefined)
			return
		}

		if (modalType === 'members') setShowModalGroup(false)
		else setShowModalAccess(false)

		setGroupItem(undefined)
	}

	const createGroupMembers = async (data: UserGroupFormData) => {
		try {
			const { groupName, description, selectedUsers } = data

			const group = await createUserGroupMutateAsync({
				name: groupName,
				description,
			})

			await Promise.all(
				selectedUsers.map((selectedUser) =>
					createUserGroupMemberMutateAsync({
						groupId: group.id,
						userId: selectedUser.user.id,
						loanRole: selectedUser.assignedRole as LoanRole,
					})
				)
			)

			await refetchUserGroups()

			alert(
				formatMessage({
					id: 'alerts.user_groups_create_success',
				})
			)

			handleCloseModal('members')
		} catch (error) {
			captureException(error)
			alert(
				formatMessage({
					id: 'alerts.user_groups_create_failed',
				}),
				{
					severity: 'error',
				}
			)
		}
	}

	const updateGroupBasicInfo = async (
		groupId: string,
		name: string,
		description: string
	): Promise<void> => {
		await updateUserGroupMutateAsync({
			groupId,
			name,
			description,
		})
	}

	const removeUsersFromGroup = async (
		groupId: string,
		usersToRemove: UserRole[]
	): Promise<void> => {
		if (usersToRemove.length === 0) return

		await Promise.all(
			usersToRemove.map((userToRemove) =>
				removeUserGroupMemberMutateAsync({
					groupId,
					userId: userToRemove.user.id,
				})
			)
		)
	}

	const updateUsersInGroup = async (
		groupId: string,
		usersToUpdate: UserRole[],
		newUsers: UserRole[]
	): Promise<void> => {
		if (usersToUpdate.length === 0) return

		await Promise.all(
			usersToUpdate.map((userToUpdate) =>
				removeUserGroupMemberMutateAsync({
					groupId,
					userId: userToUpdate.user.id,
				})
			)
		)

		await Promise.all(
			usersToUpdate.map((userToUpdate) => {
				const newUserData = newUsers.find(
					(newUser) => newUser.user.id === userToUpdate.user.id
				)
				if (newUserData) {
					return createUserGroupMemberMutateAsync({
						groupId,
						userId: userToUpdate.user.id,
						loanRole: newUserData.assignedRole as LoanRole,
					})
				}
				return Promise.resolve()
			})
		)
	}

	const addUsersToGroup = async (
		groupId: string,
		usersToAdd: UserRole[]
	): Promise<void> => {
		if (usersToAdd.length === 0) return

		await Promise.all(
			usersToAdd.map((userToAdd) =>
				createUserGroupMemberMutateAsync({
					groupId,
					userId: userToAdd.user.id,
					loanRole: userToAdd.assignedRole as LoanRole,
				})
			)
		)
	}

	const editGroupMembers = async (
		id: string,
		data: UserGroupFormData
	) => {
		try {
			const { groupName, description, selectedUsers } = data
			const currentUsers = groupMembers || []
			const newUsers = selectedUsers || []

			const usersToRemove = currentUsers.filter(
				(currentUser) =>
					!newUsers.some(
						(newUser) => newUser.user.id === currentUser.user.id
					)
			)

			const usersToUpdate = currentUsers.filter((currentUser) => {
				const matchingNewUser = newUsers.find(
					(newUser) => newUser.user.id === currentUser.user.id
				)
				return (
					matchingNewUser &&
					matchingNewUser.assignedRole !== currentUser.assignedRole
				)
			})

			const usersToAdd = newUsers.filter(
				(newUser) =>
					!currentUsers.some(
						(currentUser) => currentUser.user.id === newUser.user.id
					)
			)

			await updateGroupBasicInfo(id, groupName, description)

			await Promise.all([
				removeUsersFromGroup(id, usersToRemove),
				addUsersToGroup(id, usersToAdd),
			])

			await updateUsersInGroup(id, usersToUpdate, newUsers)

			await refetchUserGroups()

			alert(
				formatMessage({
					id: 'alerts.user_groups_update_success',
				})
			)

			handleCloseModal('members')
		} catch (error) {
			captureException(error)

			alert(
				formatMessage({
					id: 'alerts.user_groups_update_failed',
				}),
				{
					severity: 'error',
				}
			)
		}
	}

	const handleSubmitUserGroupMembers = async (
		data: UserGroupFormData
	) => {
		if (!groupItem) createGroupMembers(data)
		else editGroupMembers(groupItem.id, data)
	}

	const handleSubmitAccessGroup = async (
		accessItems: UserGroupScopeItem[]
	) => {
		if (!groupItem) return

		try {
			const currentAccessItems = groupScopes || []
			const newAccessItems = accessItems || []

			const accessItemsToRemove = currentAccessItems.filter(
				(currentItem) =>
					!newAccessItems.some(
						(newItem) => newItem.id === currentItem.id
					)
			)

			await Promise.all(
				accessItemsToRemove.map((itemToRemove) =>
					removeUserGroupScopeMutateAsync({
						groupId: groupItem.id,
						scopeId: itemToRemove.id,
					})
				)
			)

			const accessItemsToAdd = newAccessItems.filter(
				(newItem) =>
					!currentAccessItems.some(
						(currentItem) => currentItem.id === newItem.id
					)
			)

			await Promise.all(
				accessItemsToAdd.map((itemToAdd) =>
					createUserGroupScopeMutateAsync({
						groupId: groupItem.id,
						scopeType: itemToAdd.type,
						branchId:
							itemToAdd.type === 'Branch'
								? itemToAdd.value
								: undefined,
						userId:
							itemToAdd.type === 'User' ? itemToAdd.value : undefined,
					})
				)
			)

			alert(
				formatMessage({
					id: 'alerts.user_groups_access_update_success',
				})
			)

			handleCloseModal('access')
		} catch (error) {
			captureException(error)
			alert(
				formatMessage({
					id: 'alerts.user_groups_access_update_failed',
				}),
				{
					severity: 'error',
				}
			)
		}
	}

	const handleDeleteGroup = (group: UserGroupItem) => {
		setDeletingGroup(group)
		setShowModalDelete(true)
	}

	const handleConfirmGroupDelete = async () => {
		if (!deletingGroup) return

		setIsDeletingGroup(true)

		try {
			await TheBigPOSApi.deleteUserGroup(deletingGroup.id)
			refetchUserGroups()
		} finally {
			setIsDeletingGroup(false)
			setShowModalDelete(false)
			setDeletingGroup(undefined)
		}
	}

	const handleCloseGroupDeleteModal = () => {
		setShowModalDelete(false)
		setDeletingGroup(undefined)
	}

	const handleSearchTextChange = (
		e: React.ChangeEvent<HTMLInputElement>
	) => {
		handleSearchChange(e)
	}

	const handleClearSearchTextChange = () => {
		handleClearSearchClick()
	}

	const columns: TableColumn<UserGroupItem>[] = [
		{
			name: formatMessage({
				id: 'user_groups.table.columns.group_name',
			}),
			selector: (row) => row.name,
			sortable: true,
		},
		{
			name: formatMessage({
				id: 'user_groups.table.columns.description',
			}),
			selector: (row) => row.description ?? '',
		},
		{
			name: formatMessage({ id: 'global.created' }),
			selector: (row) => formatDate(row.createdAt, false),
			sortable: true,
		},
	]

	const actionItems = [
		{
			name: formatMessage({
				id: 'user_groups.table.actions.edit_members',
			}),
			onClick: (_e: React.MouseEvent, row: UserGroupItem) =>
				handleEditGroup(row, 'members'),
		},
		{
			name: formatMessage({
				id: 'user_groups.table.actions.edit_access',
			}),
			onClick: (_e: React.MouseEvent, row: UserGroupItem) =>
				handleEditGroup(row, 'access'),
		},
		{
			name: formatMessage({
				id: 'user_groups.table.actions.delete_group',
			}),
			onClick: (_e: React.MouseEvent, row: UserGroupItem) =>
				handleDeleteGroup(row),
		},
	]

	return (
		<>
			<DataTable
				keyField="id"
				title={tableTitle}
				data={userGroups?.rows || []}
				columns={columns}
				defaultSortAsc={DEFAULT_SORT_DIRECTION !== 'desc'}
				defaultSortFieldId={DEFAULT_SORT_BY}
				progressPending={isLoading}
				pagination
				paginationServer
				paginationTotalRows={userGroups?.count}
				actionItems={actionItems}
				addNewBtn={{
					text: formatMessage({
						id: 'user_groups.add_group',
					}),
					onClick: handleAddNewGroup,
				}}
				onSearchChange={handleSearchTextChange}
				searchText={searchText}
				onClearSearchClick={handleClearSearchTextChange}
				onRefreshClick={refetchUserGroups}
				onChangePage={handlePageChange}
				onChangeRowsPerPage={handleRowsPerPageChange}
				onSort={handleSort}
			/>

			<ModalUserGroupsMembers
				visible={isModalGroupMembersVisible}
				onClose={() => handleCloseModal('members')}
				isLoading={isFetchingGroupMembers}
				submit={{
					isLoading: isSubmittingUserGroupMembers,
					onSubmit: handleSubmitUserGroupMembers,
				}}
				initialValues={{
					groupName: groupItem?.name ?? '',
					description: groupItem?.description ?? '',
					selectedUsersRoles: groupMembers,
				}}
			/>

			<ModalUserGroupsAccess
				visible={showModalAccess && !isFetchingGroupScopes}
				onClose={() => handleCloseModal('access')}
				isSubmitting={isSubmittingScope}
				onSubmit={handleSubmitAccessGroup}
				initialAccessItems={groupScopes}
			/>

			<ModalUserGroupsDelete
				visible={showModalDelete}
				onClose={handleCloseGroupDeleteModal}
				onSubmit={handleConfirmGroupDelete}
				isSubmitting={isDeletingGroup}
				group={deletingGroup}
			/>
		</>
	)
}

export default GroupSharingList
